import { useEffect, useRef, useState } from "react";
import { useWatch, type FieldValues, type UseFormReturn } from "react-hook-form";
import { useDolfinClient, useDolfinAuth } from "@/components/DolfinProvider";
import type { components } from "@dolfin/api-admin";

type CustomerResponse = components["schemas"]["CustomerResponse"];
type ProductResponse = components["schemas"]["ProductResponse"];
type CurrencyResponse = components["schemas"]["CurrencyResponse"];

export interface TaxRateRow {
  id: string;
  code: string;
  name: string;
  rate: number;
  source: "org" | "country";
  isReadOnly: boolean;
}

const RESOURCE_LIMIT = 500;

export function useInvoiceResources() {
  const api = useDolfinClient();
  const { organisationId } = useDolfinAuth();
  const [customers, setCustomers] = useState<CustomerResponse[]>([]);
  const [products, setProducts] = useState<ProductResponse[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyResponse[]>([]);
  const [taxRates, setTaxRates] = useState<TaxRateRow[]>([]);
  const [defaultCurrency, setDefaultCurrency] = useState<string | null>(null);

  const [newCustomerOpen, setNewCustomerOpen] = useState(false);
  const [newProductOpen, setNewProductOpen] = useState(false);

  // Pages with searchable pickers pass `includeLists: false` and fetch on demand instead of loading the catalogues.
  async function fetchAll(options?: { includeLists?: boolean }) {
    const includeLists = options?.includeLists ?? true;
    const emptyRes = Promise.resolve({ data: null });
    const [customersRes, productsRes, currenciesRes, taxRatesRes, organisationRes] =
      await Promise.all([
        includeLists
          ? api.GET("/v1/customers", { params: { query: { Limit: RESOURCE_LIMIT } } })
          : emptyRes,
        includeLists
          ? api.GET("/v1/products", { params: { query: { Limit: RESOURCE_LIMIT } } })
          : emptyRes,
        api.GET("/v1/currencies/available"),
        api.GET("/v1/tax-rates"),
        organisationId
          ? api.GET("/v1/organisations/{id}", { params: { path: { id: organisationId } } })
          : Promise.resolve({ data: null }),
      ]);

    const loaded = {
      customers: (customersRes.data as any)?.data ?? [],
      products: (productsRes.data as any)?.data ?? [],
      currencies: (currenciesRes.data as CurrencyResponse[]) ?? [],
      taxRates: ((taxRatesRes.data as any)?.data as TaxRateRow[]) ?? [],
      defaultCurrency: ((organisationRes.data as any)?.defaultCurrency ?? null) as string | null,
    };

    if (includeLists) {
      setCustomers(loaded.customers);
      setProducts(loaded.products);
    }
    setCurrencies(loaded.currencies);
    setTaxRates(loaded.taxRates);
    setDefaultCurrency(loaded.defaultCurrency);

    return loaded;
  }

  async function refreshCustomers() {
    const res = await api.GET("/v1/customers", { params: { query: { Limit: RESOURCE_LIMIT } } });
    setCustomers(res.data?.data ?? []);
  }

  // Pass a currency to scope to products an invoice in it can accept (eng-docs "AD - Products Per-Currency").
  async function refreshProducts(currency?: string) {
    const query = currency
      ? { Limit: RESOURCE_LIMIT, Currency: currency }
      : { Limit: RESOURCE_LIMIT };
    const res = await api.GET("/v1/products", { params: { query } });
    setProducts(res.data?.data ?? []);
  }

  // So getProductPrice resolves picker-fetched products missing from the initial currency-scoped page.
  function upsertProducts(items: ProductResponse[]) {
    if (items.length === 0) return;
    setProducts((prev) => {
      const byId = new Map(prev.map((p) => [p.id, p]));
      for (const item of items) byId.set(item.id, item);
      return Array.from(byId.values());
    });
  }

  function getProductPrice(productId: string): number {
    const product = products.find((p) => p.id === productId);
    if (!product) return 0;
    return typeof product.unitPrice === "string"
      ? parseFloat(product.unitPrice)
      : (product.unitPrice ?? 0);
  }

  return {
    customers,
    products,
    currencies,
    taxRates,
    defaultCurrency,
    fetchAll,
    refreshCustomers,
    refreshProducts,
    upsertProducts,
    getProductPrice,
    newCustomerOpen,
    setNewCustomerOpen,
    newProductOpen,
    setNewProductOpen,
  };
}

// Clears selected products when the invoice currency changes: a product priced in the old one can't stay.
// Skipped on the initial load (prev null), so an edit form keeps its line items.
export function useCurrencyScopedProducts<T extends FieldValues>(form: UseFormReturn<T>) {
  const currency = useWatch({ control: form.control, name: "currency" as never }) as unknown as
    | string
    | undefined;
  const prevCurrencyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!currency) return;
    const prev = prevCurrencyRef.current;
    prevCurrencyRef.current = currency;

    if (prev !== null && prev !== currency) {
      const items = (form.getValues("lineItems" as never) ?? []) as unknown as unknown[];
      items.forEach((_, i) => {
        form.setValue(`lineItems.${i}.productId` as never, "" as never, {
          shouldValidate: false,
        });
      });
    }
    // form identity is stable enough for our use; currency drives the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency]);
}
